import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { AnimatePresence, motion } from "motion/react";
import type { ReactElement } from "react";

import {
    computeLockState,
    isSlotVisible,
    showInlineEstimate,
    slotKey,
    SlotStatusBadge,
    StatusBadge
} from "../OnboardingShared";
import type {
    ApplicationPhase,
    OnboardingNode,
    OnboardingSubsection,
    Slot,
    SubsectionId
} from "../types";

// ─── Subsection group ────────────────────────────────────────────────────────

/**
 * Renders one parent subsection plus its visible slots, then any nested
 * children plus their visible slots. Matches the canonical visual:
 *
 *   2.  Entity existence and standing       ~10 min       Not started
 *       2.1   Certificate of Formation                    Not started
 *       2.2   Current standing certificate                Not started
 *       2.3   Governance document                         Not started
 *
 *       Beneficial ownership   ~10 min per UBO            Not started
 *       2.4   UBO photo identification                    Not started
 *       …
 */
export function SubsectionGroupRows({
    node,
    ordinal,
    applicationFrozen,
    applicationPhase,
    triggers,
    slotNumberByKey,
    focusedId,
    focusedSlot,
    collapsedSlotIdxByParent,
    onSelectSubsection,
    onSelectSlot,
    celebratingIds
}: {
    node: OnboardingNode;
    ordinal: number;
    applicationFrozen: boolean;
    applicationPhase: ApplicationPhase;
    triggers: Record<string, boolean> | undefined;
    slotNumberByKey: Map<string, string>;
    focusedId: SubsectionId | null;
    focusedSlot: { parentId: SubsectionId; slotIdx: number } | null;
    collapsedSlotIdxByParent: Map<SubsectionId, number>;
    onSelectSubsection: (id: SubsectionId) => void;
    onSelectSlot: (parentId: SubsectionId, slotIdx: number) => void;
    celebratingIds?: Set<SubsectionId>;
}): ReactElement {
    const parentCollapsed = collapsedSlotIdxByParent.has(node.subsection.id);
    return (
        <>
            <ListRow
                subsection={node.subsection}
                ordinal={ordinal}
                isChild={false}
                applicationFrozen={applicationFrozen}
                applicationPhase={applicationPhase}
                focused={
                    focusedId === node.subsection.id &&
                    (!focusedSlot || parentCollapsed)
                }
                onSelect={() => onSelectSubsection(node.subsection.id)}
                preconditions={node.preconditions}
                celebrating={celebratingIds?.has(node.subsection.id)}
            />
            {!parentCollapsed &&
                node.subsection.slots.map((slot, slotIdx) => {
                    if (!isSlotVisible(slot, triggers)) return null;
                    const number = slotNumberByKey.get(
                        slotKey(node.subsection.id, slotIdx)
                    );
                    if (!number) return null;
                    return (
                        <SlotRow
                            key={`${node.subsection.id}-${slotIdx}`}
                            parent={node.subsection}
                            slot={slot}
                            number={number}
                            applicationPhase={applicationPhase}
                            focused={
                                focusedSlot?.parentId === node.subsection.id &&
                                focusedSlot.slotIdx === slotIdx
                            }
                            onSelect={() =>
                                onSelectSlot(node.subsection.id, slotIdx)
                            }
                        />
                    );
                })}
            {node.children.map((child) => {
                const childNumber = slotNumberByKey.get(child.id);
                const childCollapsed = collapsedSlotIdxByParent.has(child.id);
                return (
                    <div key={child.id}>
                        <ListRow
                            subsection={child}
                            ordinal={0}
                            childNumber={childNumber}
                            isChild
                            applicationFrozen={applicationFrozen}
                            applicationPhase={applicationPhase}
                            focused={
                                focusedId === child.id &&
                                (!focusedSlot || childCollapsed)
                            }
                            onSelect={() => onSelectSubsection(child.id)}
                            celebrating={celebratingIds?.has(child.id)}
                        />
                        {!childCollapsed &&
                            child.slots.map((slot, slotIdx) => {
                                if (!isSlotVisible(slot, triggers)) return null;
                                const number = slotNumberByKey.get(
                                    slotKey(child.id, slotIdx)
                                );
                                if (!child.slotsAsItems && !number) return null;
                                return (
                                    <SlotRow
                                        key={`${child.id}-${slotIdx}`}
                                        parent={child}
                                        slot={slot}
                                        number={number ?? ""}
                                        applicationPhase={applicationPhase}
                                        focused={
                                            focusedSlot?.parentId ===
                                                child.id &&
                                            focusedSlot.slotIdx === slotIdx
                                        }
                                        onSelect={() =>
                                            onSelectSlot(child.id, slotIdx)
                                        }
                                    />
                                );
                            })}
                    </div>
                );
            })}
        </>
    );
}

// ─── FocusDot ────────────────────────────────────────────────────────────────

/**
 * The accent dot that marks the focused row on the left list. When `focused`
 * goes true, an 8px ember dot animates in at the row's left edge and the
 * sibling content slides right to make room (the parent flex's gap-3 gives
 * the dot 0.75rem of breathing room from the next element). Replaces the
 * earlier bg-surface-sunken background tint.
 */
function FocusDot({ focused }: { focused: boolean }): ReactElement {
    return (
        <AnimatePresence initial={false}>
            {focused && (
                <motion.span
                    key="dot"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "0.5rem", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{
                        duration: 0.2,
                        ease: easings.outCubic
                    }}
                    className="overflow-hidden flex items-center shrink-0"
                    aria-hidden
                >
                    <IndicationUnit variant="solid" color="accent" size={8} />
                </motion.span>
            )}
        </AnimatePresence>
    );
}

// ─── ListRow (subsection) ───────────────────────────────────────────────────

function ListRow({
    subsection,
    ordinal,
    childNumber,
    isChild,
    applicationFrozen,
    applicationPhase,
    focused,
    onSelect,
    preconditions,
    celebrating = false
}: {
    subsection: OnboardingSubsection;
    ordinal: number;
    /** Numeric prefix for child subsections — e.g. "2.4" for Ultimate
     *  Beneficiaries nested under legal-entity. Computed at render
     *  time from the parent's slot sequence. Ignored when `isChild`
     *  is false (top-level rows use `ordinal`). */
    childNumber?: string;
    isChild: boolean;
    applicationFrozen: boolean;
    applicationPhase: ApplicationPhase;
    focused: boolean;
    onSelect: () => void;
    preconditions?: OnboardingNode["preconditions"];
    /** When true, swap the right-side StatusBadge for a row-tall
     *  success-base checkmark. Driven by the approval celebration
     *  stagger orchestrated in OnboardingHub. */
    celebrating?: boolean;
}): ReactElement {
    const node: OnboardingNode = {
        subsection,
        children: [],
        preconditions
    };
    const lock = computeLockState(node, applicationFrozen);

    // Title + ordinal render in `text-foreground-base` regardless of
    // lock state — the StatusBadge in the row's right slot is the sole
    // state cue. Standardized away from the old 3-tier `titleTone` per
    // May 2026 cleanup.
    const rowPadding = isChild ? "pl-7" : "";

    return (
        <motion.div layout="position">
            <button
                type="button"
                onClick={onSelect}
                className={`w-full text-left transition-colors rounded-xs ${rowPadding} ${
                    isChild ? "py-2" : "py-2.5"
                } ${focused ? "" : "group"}`}
                aria-pressed={focused}
            >
                <div className="flex items-center gap-3">
                    <FocusDot focused={focused} />
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0 flex-1">
                        {!isChild && (
                            <Text
                                variant="h3"
                                render={<span />}
                                className="leading-none transition-colors text-foreground-base group-hover:text-accent-base"
                            >
                                {ordinal}.
                            </Text>
                        )}
                        {isChild && childNumber && (
                            <Text
                                variant="small"
                                render={<span />}
                                className="font-numeric leading-none w-4 shrink-0 transition-colors text-foreground-base group-hover:text-accent-base"
                            >
                                {childNumber}
                            </Text>
                        )}
                        <Text
                            variant={isChild ? "small" : "h3"}
                            render={<h4 />}
                            className="leading-tight transition-colors text-foreground-base group-hover:text-accent-base"
                        >
                            {subsection.title}
                        </Text>
                        {showInlineEstimate(subsection, lock.isLocked) && (
                            <Text
                                variant="label"
                                className="leading-none text-foreground-neutral transition-colors group-hover:text-accent-base"
                            >
                                {subsection.estimateLabel}
                            </Text>
                        )}
                    </div>
                    {/* Status slot: fixed height container so the
                        celebration swap doesn't grow the row. The
                        checkmark is sized to match the badge's natural
                        line-height; both children absolute-position to
                        the right edge so the badge and check overlap
                        cleanly during the cross-fade. */}
                    <span className="relative h-[1lh] w-fit shrink-0 flex items-center">
                        <AnimatePresence initial={false}>
                            {celebrating ? (
                                <motion.span
                                    key="celebrate"
                                    initial={{ opacity: 0, scale: 0.7 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.7 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: easings.outCubic
                                    }}
                                    className="absolute inset-y-0 right-0 flex items-center"
                                    aria-label="Approved"
                                >
                                    <RowCheckmark />
                                </motion.span>
                            ) : (
                                <motion.span
                                    key="badge"
                                    initial={{ opacity: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{
                                        duration: 0.2,
                                        ease: easings.outCubic
                                    }}
                                    className="absolute inset-y-0 right-0 flex items-center"
                                >
                                    <StatusBadge
                                        subsection={subsection}
                                        isLocked={lock.isLocked}
                                        phase={applicationPhase}
                                        hoverable={!focused}
                                    />
                                </motion.span>
                            )}
                        </AnimatePresence>
                        {/* Invisible spacer holding the slot's min width
                            to whichever variant happens to be wider — the
                            badge text "Approved" beats the checkmark
                            glyph, so we render the badge invisibly to
                            reserve space and prevent horizontal jitter. */}
                        <span
                            className="invisible pointer-events-none"
                            aria-hidden
                        >
                            <StatusBadge
                                subsection={subsection}
                                isLocked={lock.isLocked}
                                phase={applicationPhase}
                            />
                        </span>
                    </span>
                </div>
            </button>
        </motion.div>
    );
}

/**
 * Row checkmark for the approval-celebration stagger. Sized to the
 * current line height (1lh) so it occupies the same vertical envelope
 * as the StatusBadge it replaces — no row growth, no layout shift.
 */
function RowCheckmark(): ReactElement {
    return (
        <svg
            viewBox="0 0 32 32"
            fill="none"
            className="h-[1lh] w-[1lh] text-success-base"
            aria-hidden
        >
            <motion.path
                d="M6 16 L13 23 L26 9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{
                    duration: 0.4,
                    ease: easings.outCubic
                }}
            />
        </svg>
    );
}

// ─── SlotRow ────────────────────────────────────────────────────────────────

/**
 * One slot rendered as an indented sub-row beneath its parent subsection.
 * Anatomy:
 *   [number]   [title]                                   [slot status]
 *
 * The badge is the slot's own state (`Slot.status`, derived from the step
 * payload in `scenarioFromServer`), not the parent's — a section reading
 * "In Progress" is only useful if the row responsible for that says so.
 * Slots with nothing to derive a state from (item-list instances, provider
 * flows) render no badge, as before.
 *
 * Drafting only: post-submission the reviewer's verdict is the state that
 * matters, and it already renders as the Rejected / Additional Info Needed
 * tags beside the title.
 */
function SlotRow({
    parent,
    slot,
    number,
    applicationPhase,
    focused,
    onSelect
}: {
    parent: OnboardingSubsection;
    slot: Slot;
    number: string;
    applicationPhase: ApplicationPhase;
    focused: boolean;
    onSelect: () => void;
}): ReactElement {
    // Title + number render in `text-foreground-base` regardless of the
    // parent's lock state — the badge in the row's right slot is the sole
    // state cue. Standardized away from the old 3-tier `titleTone` per
    // May 2026 cleanup.

    return (
        <motion.div layout="position">
            <button
                type="button"
                onClick={onSelect}
                className={`w-full text-left py-2 transition-colors rounded-xs pl-7 ${
                    focused ? "" : "group"
                }`}
                aria-pressed={focused}
            >
                <div className="flex items-center gap-3">
                    <FocusDot focused={focused} />
                    <div className="flex items-center gap-x-3 min-w-0 flex-1">
                        {parent.slotsAsItems ? (
                            <span className="w-4 shrink-0 flex items-center">
                                <IndicationUnit
                                    variant="status"
                                    state="disabled"
                                    size={6}
                                />
                            </span>
                        ) : (
                            <Text
                                variant="small"
                                render={<span />}
                                className="font-numeric leading-none w-4 shrink-0 transition-colors text-foreground-base group-hover:text-accent-base"
                            >
                                {number}
                            </Text>
                        )}
                        <Text
                            variant="small"
                            className="leading-snug transition-colors text-foreground-base group-hover:text-accent-base"
                        >
                            {slot.title}
                        </Text>
                        {slot.issuerRequested && (
                            <Badge variant="error">
                                Additional Info Needed
                            </Badge>
                        )}
                        {slot.reviewState === "Rejected" && (
                            <Badge variant="error">Rejected</Badge>
                        )}
                    </div>
                    {slot.status && applicationPhase === "submission" && (
                        <span className="shrink-0">
                            <SlotStatusBadge
                                status={slot.status}
                                hoverable={!focused}
                            />
                        </span>
                    )}
                </div>
            </button>
        </motion.div>
    );
}

// SlotDetailPanel uses lock state from `computeLockState` and the rejection
// note can render directly. Don't reach into this file for those.
